import React from "react";

// Simmi's replies use a little markdown: **bold**, *italic*, [links](/page) and short "- " / "1. " lists.
// This renders just that as React elements (never raw HTML), and copes with half-written markdown while a
// reply is still streaming in (an unclosed "**" is simply hidden until its pair arrives).

// **bold** | [text](url) | *italic* or _italic_ (only between spaces/punctuation, so snake_case stays as is)
const INLINE = /\*\*([^*\n]+?)\*\*|\[([^\]\n]+)\]\(([^)\s]+)\)|(^|[\s(])[*_]([^*_\n]+?)[*_](?=[\s.,!?;:)]|$)/g;
const SAFE_URL = /^(https?:\/\/|mailto:|tel:|\/(?!\/))/i;

// What Simmi's voice says (and the live caption shows): the same words without the markdown.
export const toSpeech = (text = "") =>
  text
    .replace(/\[([^\]\n]+)\]\([^)\s]+\)/g, "$1")
    .replace(/\*\*|__|[*_#`]/g, "")
    .replace(/^\s*[-•]\s+/gm, "");

function inline(text, onNavigate, keyPrefix) {
  const out = [];
  let last = 0;
  let i = 0;
  const plain = (s) => s.replace(/\*\*/g, ""); // an unclosed "**" mid-stream
  for (const m of text.matchAll(INLINE)) {
    if (m.index > last) out.push(plain(text.slice(last, m.index)));
    const key = `${keyPrefix}-${i++}`;
    if (m[1]) {
      out.push(
        <strong key={key} className="font-semibold text-white">
          {m[1]}
        </strong>,
      );
    } else if (m[2]) {
      const href = m[3];
      if (!SAFE_URL.test(href)) out.push(m[2]);
      else if (href.startsWith("/") && onNavigate) {
        out.push(
          <button key={key} type="button" onClick={() => onNavigate(href)} className="text-[#06D3DB] underline underline-offset-2 hover:text-white">
            {m[2]}
          </button>,
        );
      } else {
        out.push(
          <a key={key} href={href} target="_blank" rel="noreferrer" className="text-[#06D3DB] underline underline-offset-2 hover:text-white">
            {m[2]}
          </a>,
        );
      }
    } else if (m[5]) {
      if (m[4]) out.push(m[4]); // the space or "(" before the opening marker
      out.push(<em key={key}>{m[5]}</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(plain(text.slice(last)));
  return out;
}

// Paragraphs, bullet lists and numbered lists; a single line break inside a paragraph stays a line break.
function blocks(text) {
  const result = [];
  let para = [];
  let list = null;
  const flushPara = () => {
    if (para.length) result.push({ type: "p", lines: para });
    para = [];
  };
  const flushList = () => {
    if (list) result.push(list);
    list = null;
  };
  for (const raw of text.split("\n")) {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
    if (bullet || numbered) {
      flushPara();
      const type = bullet ? "ul" : "ol";
      if (!list || list.type !== type) {
        flushList();
        list = { type, items: [], start: numbered ? Number(numbered[1]) : 1 };
      }
      list.items.push(bullet ? bullet[1] : numbered[2]);
    } else if (!line.trim()) {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return result;
}

const SimmiMarkdown = ({ text = "", onNavigate, className = "" }) => (
  <div className={`simmi-md space-y-2 ${className}`}>
    {blocks(text).map((b, bi) => {
      if (b.type === "p") {
        return (
          <p key={bi}>
            {b.lines.map((l, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {inline(l, onNavigate, `${bi}-${li}`)}
              </React.Fragment>
            ))}
          </p>
        );
      }
      const List = b.type;
      return (
        <List
          key={bi}
          start={b.type === "ol" ? b.start : undefined}
          className={`space-y-1 pl-5 ${b.type === "ol" ? "list-decimal marker:text-[#06D3DB]" : "list-disc marker:text-[#06D3DB]/80"}`}
        >
          {b.items.map((item, ii) => (
            <li key={ii} className="pl-0.5">
              {inline(item, onNavigate, `${bi}-${ii}`)}
            </li>
          ))}
        </List>
      );
    })}
  </div>
);

export default SimmiMarkdown;
